import React, { createContext, useContext, useState, useEffect } from 'react';

const PWAContext = createContext();

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider = ({ children }) => {
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    checkPWAStatus();
  }, []);

  const checkPWAStatus = () => {
    console.log('PWAContext: Checking PWA status...');
    
    // Check if PWA setup is complete
    const setupComplete = localStorage.getItem('hopseat_pwa_setup') === 'complete';
    
    // Check if app is running in standalone mode (installed) - More comprehensive detection
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isIOSStandalone = window.navigator.standalone === true;
    const minimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
    const installed = standalone || fullscreen || isIOSStandalone || minimalUI;
    
    // Check notification permission - More comprehensive check including OneSignal
    let notificationPermission = false;
    if ('Notification' in window) {
      notificationPermission = Notification.permission === 'granted';
    }
    
    // Also check OneSignal permission if available
    if (window.OneSignal && !window.OneSignalUnavailable) {
      try {
        // OneSignal permission check will be handled in PWA Manager
        console.log('OneSignal available for permission check');
      } catch (error) {
        console.log('OneSignal permission check error:', error);
      }
    }
    
    console.log('PWAContext: Status check results:', {
      setupComplete,
      installed,
      notificationPermission,
      standalone,
      fullscreen,
      isIOSStandalone,
      minimalUI
    });
    
    setIsPWAInstalled(installed);
    setNotificationsEnabled(notificationPermission);
    
    // More flexible setup complete logic
    // If user manually marked setup complete, trust that unless we can definitively detect it's not installed
    if (setupComplete) {
      setIsSetupComplete(true);
    } else {
      setIsSetupComplete(false);
    }
  };

  const openSetupModal = () => {
    console.log('PWAContext: Opening setup modal - current state:', { showSetupModal, isSetupComplete });
    setShowSetupModal(true);
    console.log('PWAContext: Modal state set to true');
  };

  const closeSetupModal = () => {
    setShowSetupModal(false);
  };

  const markSetupComplete = () => {
    console.log('PWAContext: Marking setup as complete...');
    localStorage.setItem('hopseat_pwa_setup', 'complete');
    localStorage.setItem('hopseat_pwa_completed_at', new Date().toISOString());
    setIsSetupComplete(true);
    setShowSetupModal(false);
    
    // Force recheck status after a delay to allow for PWA installation detection
    setTimeout(() => {
      checkPWAStatus();
      console.log('PWAContext: Setup marked complete, status rechecked');
    }, 1000);
  };

  const resetPWASetup = () => {
    localStorage.removeItem('hopseat_pwa_setup');
    setIsSetupComplete(false);
    setShowSetupModal(false);
    checkPWAStatus();
  };

  const value = {
    showSetupModal,
    isSetupComplete,
    isPWAInstalled,
    notificationsEnabled,
    openSetupModal,
    closeSetupModal,
    markSetupComplete,
    resetPWASetup,
    checkPWAStatus
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
};